import React from 'react';

/**
 * Methodology-aware score input component.
 * Adapts UI based on the active scoring methodology.
 *
 * Props:
 *   methodology: 'likelihood_impact_5x5' | 'rpn_fmea' | 'inherent_residual'
 *   values: { severity, likelihood, exposure, impact, occurrence, detection, control_design, control_operating }
 *   onChange: (field, value) => void
 *   compact: boolean — use compact layout (inline)
 */

const SEVERITY_5 = [
  { v: 1, label: 'Negligible', desc: 'Minor deviation, no member impact' },
  { v: 2, label: 'Minor', desc: 'Limited operational issue' },
  { v: 3, label: 'Moderate', desc: 'CMS notice possible' },
  { v: 4, label: 'Major', desc: 'CMS CAP likely, Star Rating impact' },
  { v: 5, label: 'Catastrophic', desc: 'CMS sanctions, widespread harm' }
];

const LIKELIHOOD_5 = [
  { v: 1, label: 'Rare', desc: '<5% per year' },
  { v: 2, label: 'Unlikely', desc: '5-20%' },
  { v: 3, label: 'Possible', desc: '20-50%' },
  { v: 4, label: 'Likely', desc: '50-80%' },
  { v: 5, label: 'Almost Certain', desc: '>80%' }
];

const SEVERITY_10 = [
  { v: 1, label: 'None' }, { v: 2, label: 'Very Minor' }, { v: 3, label: 'Minor' },
  { v: 4, label: 'Low' }, { v: 5, label: 'Moderate' }, { v: 6, label: 'Significant' },
  { v: 7, label: 'High' }, { v: 8, label: 'Very High' }, { v: 9, label: 'Extreme' },
  { v: 10, label: 'Hazardous' }
];

const OCCURRENCE_10 = [
  { v: 1, label: 'Almost Never' }, { v: 2, label: 'Remote' }, { v: 3, label: 'Very Low' },
  { v: 4, label: 'Low' }, { v: 5, label: 'Occasional' }, { v: 6, label: 'Moderate' },
  { v: 7, label: 'Frequent' }, { v: 8, label: 'High' }, { v: 9, label: 'Very High' },
  { v: 10, label: 'Certain' }
];

const DETECTION_10 = [
  { v: 1, label: 'Almost Certain' }, { v: 2, label: 'Very High' }, { v: 3, label: 'High' },
  { v: 4, label: 'Moderately High' }, { v: 5, label: 'Medium' }, { v: 6, label: 'Low' },
  { v: 7, label: 'Very Low' }, { v: 8, label: 'Remote' }, { v: 9, label: 'Very Remote' },
  { v: 10, label: 'Undetectable' }
];

// Plain-language meaning of each factor, surfaced as a tooltip + helper line.
const HINTS = {
  severity: 'How bad the harm if the risk occurs — the impact. 1 = no real effect; 10 = catastrophic (CMS sanctions, member harm).',
  occurrence: 'How likely the cause happens — the likelihood / frequency. 1 = almost never; 10 = essentially certain to occur.',
  detection: 'How likely it slips past your current controls before causing harm — the inverse of control effectiveness. 1 = controls almost always catch it; 10 = undetectable.',
  impact5: 'How bad the harm if it occurs.',
  likelihood5: 'How likely it is to occur.',
  exposure: 'How widespread the risk is — scope, volume, or value at stake.'
};

function ScoreSlider({ label, value, onChange, min, max, items, color, hint }) {
  const pct = ((value - min) / (max - min)) * 100;
  const item = items?.find(i => i.v === value);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
          {label}
          {hint && (
            <span
              title={hint}
              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-500 text-[10px] font-bold cursor-help select-none"
              aria-label={hint}
            >
              i
            </span>
          )}
        </label>
        <span className="text-sm font-bold text-slate-900">
          {value} {item && <span className="font-normal text-slate-500">— {item.label}</span>}
        </span>
      </div>
      {hint && <p className="text-[11px] text-slate-400 leading-snug">{hint}</p>}
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${color || '#3b82f6'} 0%, ${color || '#3b82f6'} ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`
        }}
      />
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

// Build generic 1..scale items when no descriptive labels exist (1-3, 1-5).
function genericItems(scale) { return Array.from({ length: scale }, (_, i) => ({ v: i + 1 })); }
function rpnItems(scale, kind) {
  if (scale === 10) return kind === 'sev' ? SEVERITY_10 : kind === 'occ' ? OCCURRENCE_10 : DETECTION_10;
  return genericItems(scale);
}

function getScoreResult(methodology, values, rpnScale = 10) {
  if (methodology === 'likelihood_impact_5x5') {
    const score = (values.impact || 0) * (values.likelihood || 0);
    const max = 25;
    let band = 'Low', color = '#16a34a';
    if (score >= 16) { band = 'Critical'; color = '#dc2626'; }
    else if (score >= 10) { band = 'High'; color = '#f97316'; }
    else if (score >= 5) { band = 'Moderate'; color = '#eab308'; }
    return { score, max, band, color };
  }
  if (methodology === 'rpn_fmea') {
    const score = (values.severity || 0) * (values.occurrence || 0) * (values.detection || 0);
    const max = rpnScale * rpnScale * rpnScale;
    // Standard FMEA classification: breakpoints at 10% / 30% / 50% of the max.
    let band = 'Low', color = '#16a34a';
    if (score > 0.5 * max) { band = 'Critical'; color = '#dc2626'; }
    else if (score > 0.3 * max) { band = 'High'; color = '#f97316'; }
    else if (score > 0.1 * max) { band = 'Moderate'; color = '#eab308'; }
    return { score, max, band, color };
  }
  // inherent_residual
  const inherent = (values.severity || 0) * (values.likelihood || 0) * (values.exposure || 0);
  const controlAvg = ((values.control_design || 0) + (values.control_operating || 0)) / 2;
  const controlStr = Math.min(controlAvg / 4, 1);
  const residual = Math.max(inherent * (1 - controlStr), 1);
  let tier = 'C', color = '#16a34a';
  if (residual >= 50) { tier = 'A'; color = '#dc2626'; }
  else if (residual >= 30) { tier = 'B'; color = '#d97706'; }
  return { score: Math.round(residual * 100) / 100, max: 125, band: `Tier ${tier}`, color, inherent };
}

export default function ScoreInput({ methodology = 'likelihood_impact_5x5', values = {}, onChange, compact = false, rpnScale = 10 }) {
  const set = (field, val) => onChange(field, val);
  const result = getScoreResult(methodology, values, rpnScale);
  const rpnMid = Math.ceil(rpnScale / 2);

  return (
    <div className="space-y-5">
      {/* Methodology label */}
      <div className="bg-slate-50 rounded-xl px-4 py-2.5 text-sm text-slate-600 flex items-center justify-between">
        <span className="font-medium">
          {methodology === 'likelihood_impact_5x5' && '5×5 Likelihood × Impact'}
          {methodology === 'rpn_fmea' && 'RPN / FMEA (Severity × Occurrence × Detection)'}
          {methodology === 'inherent_residual' && 'Inherent / Residual with Control Strength'}
        </span>
        <span className="text-xs text-slate-400">Score range: 1–{result.max}</span>
      </div>

      {/* 5x5 */}
      {methodology === 'likelihood_impact_5x5' && (
        <div className={compact ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
          <ScoreSlider
            label="Impact / Severity"
            value={values.impact || 3}
            onChange={v => set('impact', v)}
            min={1} max={5}
            items={SEVERITY_5}
            color="#ef4444"
            hint={HINTS.impact5}
          />
          <ScoreSlider
            label="Likelihood"
            value={values.likelihood || 3}
            onChange={v => set('likelihood', v)}
            min={1} max={5}
            items={LIKELIHOOD_5}
            color="#3b82f6"
            hint={HINTS.likelihood5}
          />
        </div>
      )}

      {/* RPN */}
      {methodology === 'rpn_fmea' && (
        <div className="space-y-3">
          <div className="bg-blue-50/60 border border-blue-100 rounded-xl px-4 py-3 text-xs text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-700">How RPN works:</span> RPN = Severity × Occurrence × Detection, which in plain terms is <span className="font-medium">impact × likelihood × how well your controls catch it</span>. Severity is the impact, Occurrence is the likelihood, and Detection Difficulty is the inverse of control effectiveness — a high Detection score means weak controls, so stronger controls lower the score and the overall RPN.
          </div>
          <div className={compact ? 'grid grid-cols-3 gap-4' : 'space-y-4'}>
            <ScoreSlider
              label="Severity"
              value={Math.min(values.severity || rpnMid, rpnScale)}
              onChange={v => set('severity', v)}
              min={1} max={rpnScale}
              items={rpnItems(rpnScale, 'sev')}
              color="#ef4444"
              hint={HINTS.severity}
            />
            <ScoreSlider
              label="Occurrence"
              value={Math.min(values.occurrence || rpnMid, rpnScale)}
              onChange={v => set('occurrence', v)}
              min={1} max={rpnScale}
              items={rpnItems(rpnScale, 'occ')}
              color="#f97316"
              hint={HINTS.occurrence}
            />
            <ScoreSlider
              label="Detection Difficulty"
              value={Math.min(values.detection || rpnMid, rpnScale)}
              onChange={v => set('detection', v)}
              min={1} max={rpnScale}
              items={rpnItems(rpnScale, 'det')}
              color="#8b5cf6"
              hint={HINTS.detection}
            />
          </div>
        </div>
      )}

      {/* Inherent / Residual */}
      {methodology === 'inherent_residual' && (
        <div className="space-y-4">
          <div className={compact ? 'grid grid-cols-3 gap-4' : 'space-y-4'}>
            <ScoreSlider label="Severity" value={values.severity || 3} onChange={v => set('severity', v)} min={1} max={5} items={SEVERITY_5} color="#ef4444" hint={HINTS.impact5} />
            <ScoreSlider label="Likelihood" value={values.likelihood || 3} onChange={v => set('likelihood', v)} min={1} max={5} items={LIKELIHOOD_5} color="#3b82f6" hint={HINTS.likelihood5} />
            <ScoreSlider label="Exposure" value={values.exposure || 3} onChange={v => set('exposure', v)} min={1} max={5} color="#f97316" hint={HINTS.exposure} />
          </div>
          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wide">Control Effectiveness</div>
            <div className={compact ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
              <ScoreSlider label="Control Design" value={values.control_design || 0} onChange={v => set('control_design', v)} min={0} max={4} color="#16a34a" />
              <ScoreSlider label="Control Operating" value={values.control_operating || 0} onChange={v => set('control_operating', v)} min={0} max={4} color="#16a34a" />
            </div>
          </div>
        </div>
      )}

      {/* Score result */}
      <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500">Calculated Score</div>
          <div className="text-2xl font-bold text-slate-900">
            {result.score}
            <span className="text-sm font-normal text-slate-400 ml-1">/ {result.max}</span>
          </div>
          {methodology === 'inherent_residual' && result.inherent > 0 && (
            <div className="text-xs text-slate-400 mt-0.5">Inherent: {result.inherent}</div>
          )}
        </div>
        <div
          className="px-4 py-2 rounded-xl text-sm font-bold text-white"
          style={{ backgroundColor: result.color }}
        >
          {result.band}
        </div>
      </div>
    </div>
  );
}
